import React from 'react'
import { formatMoney } from 'accounting'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTimesCircle, faPlus, faEyeSlash } from '@fortawesome/pro-light-svg-icons'

import {
  Box, Button, Checkbox, ListItem, SortableList, Text,
} from '@domain-group/suit-theme'

import handleCallbackAction from '@functions/handleCallbackAction'
import useCampaignQuoteTemplateSection from '@hooks/useCampaignQuoteTemplateSection'
import useSelector from '@hooks/useSelector'

import type { CampaignQuoteTemplateSectionModel } from '@models/types'

import HintWrapper from '@components/HintWrapper'

import CampaignQuoteTemplateItem from '../CampaignQuoteTemplateItem'

interface CampaignQuoteTemplateSectionProps {
  callbacks: {
    showCreateOrEditQuoteItemModal: VoidFunction,
    showCreateOrEditQuoteSectionModal: VoidFunction,
  },
  campaignQuoteTemplateSection: CampaignQuoteTemplateSectionModel,
  campaignQuoteTemplateSectionIds: number[],
  first: boolean,
  index: number,
  last: boolean,
  lastIndex: number,
  sortedCampaignQuoteTemplateItemIds: number[],
}

const CampaignQuoteTemplateSection = React.forwardRef((props: CampaignQuoteTemplateSectionProps, ref) => {
  const {
    callbacks,
    campaignQuoteTemplateSection,
    campaignQuoteTemplateSectionIds,
    first,
    index,
    last,
    lastIndex,
    sortedCampaignQuoteTemplateItemIds,
  } = props

  const campaignQuoteTemplateSectionPayload = useCampaignQuoteTemplateSection(campaignQuoteTemplateSection)
  const {
    callbacks: {
      addCustomItem,
      addOrderableItem,
      addSectionAtIndex,
      createOrEditCampaignQuoteTemplateSection,
    },
    campaignQuoteTemplateSection: {
      footer,
      header,
      id,
      title,
    },
    hideDates,
    hidePrices,
    showSubtotal,
    subtotal,
  } = campaignQuoteTemplateSectionPayload

  const campaignQuoteTemplateItems = useSelector(reduxState => reduxState.entities.campaignQuoteTemplateItems)

  return (
    <Box flexDirection="column" padding="large" ref={ref}>
      <ListItem
        boxProps={{ paddingY: 'large' }}
        onClick={() => createOrEditCampaignQuoteTemplateSection({
          first, index, last, campaignQuoteTemplateSectionIds,
        })}
        showAngleIcon
      >
        <Text fontSize="large">{title}</Text>

        <Text fontSize="large" marginLeft="auto">
          <HintWrapper
            hint="Sub Total will not display."
          >
            {!showSubtotal && (
              <FontAwesomeIcon
                icon={faEyeSlash}
                style={{
                  color: '#8c8c8e', fontSize: '12px', marginRight: '4px', verticalAlign: 'middle',
                }}
              />
            )}
          </HintWrapper>

          {formatMoney(subtotal)}
        </Text>
      </ListItem>

      {header && (
        <Text color="bodyFontLightColor" fontSize="small" lineHeight="1.3" paddingY="large">
          {header}
        </Text>
      )}

      <SortableList.Dropzone droppableId={`Section-${id}`}>
        <Box flexDirection="column">
          {!sortedCampaignQuoteTemplateItemIds.length && (
            <ListItem
              boxProps={{
                alignItems: 'center',
                backgroundColor: 'white',
                borderBottom: '1px dashed',
                borderColor: 'lineColor',
                paddingY: 'medium',
              }}
              onClick={() => addOrderableItem()}
              showAngleIcon
            >
              <Checkbox checked={false} iconDefault={faTimesCircle} />

              <Box flexDirection="column">
                <Text color="bodyFontColor" fontSize="small" lineHeight="1.3">
                  No items added to sections
                </Text>
              </Box>

              <Text color="bodyFontLightColor" fontSize="small" lineHeight="1.3" marginLeft="auto">
                Add
              </Text>
            </ListItem>
          )}

          {sortedCampaignQuoteTemplateItemIds.map((quoteItemId, itemIndex) => {
            const quoteItem = campaignQuoteTemplateItems[quoteItemId] || {}

            return (
              <SortableList.Item draggableId={`Item-${quoteItem.id}`} key={quoteItem.id} index={itemIndex}>
                <CampaignQuoteTemplateItem
                  // @ts-expect-error TS2322
                  callbacks={callbacks}
                  campaignQuoteTemplateItem={quoteItem}
                  hideDates={hideDates}
                  hidePrices={hidePrices}
                />
              </SortableList.Item>
            )
          })}
        </Box>
      </SortableList.Dropzone>

      {footer && (
        <Text color="bodyFontLightColor" fontSize="small" lineHeight="1.3" paddingY="large">
          {footer}
        </Text>
      )}

      <Box borderTop="1px dashed" borderColor="lineColor" flexDirection={['column', 'row']} paddingTop="large">
        <Box width={['100%', 'auto']}>
          <Button
            buttonStyle="secondaryUtility"
            icon={<FontAwesomeIcon icon={faPlus} />}
            onClick={() => handleCallbackAction({
              callbacks: {
                // @ts-expect-error TS2322
                action: addSectionAtIndex,
                buildActionParams: () => [{ campaignQuoteTemplateSectionIds, index, lastIndex }],
              },
              toastText: 'Quote Template Section Created',
            })}
            size="small"
            //@ts-expect-error
            width="auto"
          >
            New Section
          </Button>
        </Box>

        <Box marginTop={['medium', 0]} marginLeft="auto" width={['100%', 'auto']}>
          <Button
            buttonStyle="secondaryEdit"
            icon={<FontAwesomeIcon icon={faPlus} />}
            onClick={() => addCustomItem()}
            //@ts-expect-error
            marginRight="medium"
            size="small"
            width="auto"
          >
            Add Custom Item
          </Button>

          <Button
            buttonStyle="primaryEdit"
            icon={<FontAwesomeIcon icon={faPlus} />}
            onClick={() => addOrderableItem()}
            size="small"
            //@ts-expect-error
            width="auto"
          >
            Add Order Item
          </Button>
        </Box>
      </Box>
    </Box>
  )
})

export default CampaignQuoteTemplateSection
